import type { AssetClass } from '../generated';

/**
 * Identity helper that accepts an asset-class list only if it is exhaustive.
 * Each entry must be an `AssetClass` (via the `T` constraint) *and* no union
 * member may be left out — an omission makes `Exclude<...>` non-`never`, which
 * collapses the parameter type to `never` and fails at the array literal below.
 */
const asExhaustiveAssetClasses = <const T extends readonly AssetClass[]>(
  classes: T & (Exclude<AssetClass, T[number]> extends never ? unknown : never),
): T => classes;

/**
 * Every member of the generated `AssetClass` union, as a runtime array.
 *
 * `AssetClass` is a type-only union (see `../generated/types.gen.ts`), so it
 * can't be enumerated at runtime — this list is the one hand-maintained mirror
 * of it, and everything else that needs asset-class labels derives from here.
 * `asExhaustiveAssetClasses` turns drift into a compile error, so a class added
 * to the spec and pulled in by `pnpm generate` fails typecheck here rather than
 * silently going missing from pickers and URL validation.
 *
 * Order is the union's own declaration order and carries no meaning — consumers
 * that display these sort them (see `CANONICAL_ASSET_CLASSES`).
 */
export const ALL_ASSET_CLASSES = asExhaustiveAssetClasses([
  'Lifestyle',
  'Feature/Benefit',
  'Before and After',
  'Product Close Up',
  'Competitor Comparison',
  'Product Family',
  'Hero Image',
  'Multi-Pack',
  'Included Items',
  'Alternate Product Views',
  'Endorsements',
  'Certification Mark',
  'Usage Instructions',
  'Size and Scale',
  'Product Facts Panel',
]);

/**
 * `Hero Image` is a slot decision (hero vs. carousel), not a classification, so
 * it is never offered as a pickable class — the BE rejects the case (SPM-2904)
 * and we'd rather not make the user recover from an error toast.
 */
export type SelectableAssetClass = Exclude<AssetClass, 'Hero Image'>;

/**
 * The 14 user-selectable asset classes — alphabetically sorted.
 *
 * Shared by the product page's asset-class dropdown and the Explore
 * image-feedback modal's classification picker so the two stay in sync. This
 * array's order is the canonical display order — consumers render/sort by it
 * rather than re-sorting their own copy, so there's a single place the ordering
 * lives. Derived from `ALL_ASSET_CLASSES` so new classes appear here
 * automatically.
 *
 * Frozen because it's a module-level singleton shared across packages: the
 * `readonly` type stops `push`/`sort`/`splice` at compile time, and the freeze
 * stops anything that reaches it through a widened or untyped reference from
 * reordering every consumer's list at runtime.
 */
export const CANONICAL_ASSET_CLASSES: readonly SelectableAssetClass[] = Object.freeze(
  ALL_ASSET_CLASSES.filter(
    (assetClass): assetClass is SelectableAssetClass => assetClass !== 'Hero Image',
    // `localeCompare` rather than the default codepoint sort so entries like
    // `Feature/Benefit` order the way a reader expects, and so consumers that
    // compare labels locale-aware get the same sequence as this list.
  ).sort((a, b) => a.localeCompare(b)),
);
